'use client'

import { useRef, useMemo, useEffect } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import {
  Fn,
  Loop,
  instancedArray,
  instanceIndex,
  uniform,
  uniformArray,
  vec2,
  vec3,
  float,
  texture,
  hash,
  mix,
  mod,
  max,
  min,
  step,
  sin,
  abs,
  sign,
  dot,
  smoothstep,
  color,
  positionGeometry,
  rotateUV,
  uv,
} from 'three/tsl'

import { wind } from './Wind'
import { createPerlinNoiseTexture } from './noiseTexture'
import { useObstacles } from '../../obstacles/ObstacleProvider'

const DEFAULT_COUNT = 512
const DEFAULT_SIZE = 80 // area in world units
const DEBRIS_SCALE = 0.25

// -- Debris types --
// 0 = leaf, 1 = crisp packet, 2 = newspaper

// Obstacle AABBs now come from the shared ObstacleProvider (useObstacles hook).

// -- Branchless 3-way type selection helpers --
function typeWeights(typeNode) {
  const isCrisp = step(float(0.5), typeNode).mul(step(typeNode, float(1.5)))
  const isNewspaper = step(float(1.5), typeNode)
  const isLeaf = float(1.0).sub(isCrisp).sub(isNewspaper)
  return { isLeaf, isCrisp, isNewspaper }
}

function selectByType(typeNode, leafVal, crispVal, newsVal) {
  const { isLeaf, isCrisp, isNewspaper } = typeWeights(typeNode)
  return leafVal.mul(isLeaf).add(crispVal.mul(isCrisp)).add(newsVal.mul(isNewspaper))
}

// Hardcoded debris config (previously Leva controls)
const DEBRIS_CONFIG = {
  areaSize: DEFAULT_SIZE,
  gravity: 9.807,
  windMultiplier: 0.5,
  upwardMultiplier: 1.0,
  damping: 1.5,
}

export default function Debris({ count = DEFAULT_COUNT }) {
  const { gl } = useThree()
  const initDone = useRef(false)

  const debrisCount = count
  const { areaSize, gravity, windMultiplier, upwardMultiplier, damping } = DEBRIS_CONFIG

  // Pull obstacle OBBs from the shared provider
  const { obstacles, isLoaded: obstaclesLoaded } = useObstacles()

  // Pack OBBs into vec4 arrays: center.xyz + w (unused), axisX.xyz + w = halfExtent.x, etc.
  const { obstacleCenters, obstacleAxisX, obstacleAxisY, obstacleAxisZ, numObstacles } = useMemo(() => {
    const centers = []
    const axisXs = []
    const axisYs = []
    const axisZs = []
    obstacles.forEach((c) => {
      centers.push(new THREE.Vector4(c.center.x, c.center.y, c.center.z, 0))
      axisXs.push(new THREE.Vector4(c.axisX.x, c.axisX.y, c.axisX.z, c.halfExtents.x))
      axisYs.push(new THREE.Vector4(c.axisY.x, c.axisY.y, c.axisY.z, c.halfExtents.y))
      axisZs.push(new THREE.Vector4(c.axisZ.x, c.axisZ.y, c.axisZ.z, c.halfExtents.z))
    })
    // Fallback: one degenerate OBB so the uniformArray has valid shape.
    if (centers.length === 0) {
      centers.push(new THREE.Vector4(0, -10000, 0, 0))
      axisXs.push(new THREE.Vector4(1, 0, 0, 0))
      axisYs.push(new THREE.Vector4(0, 1, 0, 0))
      axisZs.push(new THREE.Vector4(0, 0, 1, 0))
    }
    return {
      obstacleCenters: centers,
      obstacleAxisX: axisXs,
      obstacleAxisY: axisYs,
      obstacleAxisZ: axisZs,
      numObstacles: centers.length,
    }
  }, [obstacles])

  // Uniforms for compute shader
  const uniforms = useRef({
    size: uniform(areaSize),
    gravity: uniform(gravity),
    windMultiplier: uniform(windMultiplier),
    upwardMultiplier: uniform(upwardMultiplier),
    damping: uniform(damping),
    deltaTime: uniform(0),
    bendTime: uniform(0),
    focusPoint: uniform(new THREE.Vector2(0, 0)),
    scale: uniform(DEBRIS_SCALE),
    windFrequency: uniform(0.005),
    rotationFrequency: uniform(3.0),
    rotationElevationMultiplier: uniform(1.0),
    // Obstacle OBBs (center + 3 world-space basis axes with halfExtent in .w)
    obstacleCenters: uniformArray(obstacleCenters, 'vec4'),
    obstacleAxisX: uniformArray(obstacleAxisX, 'vec4'),
    obstacleAxisY: uniformArray(obstacleAxisY, 'vec4'),
    obstacleAxisZ: uniformArray(obstacleAxisZ, 'vec4'),
  })

  // Sync leva controls to uniforms
  useEffect(() => { uniforms.current.size.value = areaSize }, [areaSize])
  useEffect(() => { uniforms.current.gravity.value = gravity }, [gravity])
  useEffect(() => { uniforms.current.windMultiplier.value = windMultiplier }, [windMultiplier])
  useEffect(() => { uniforms.current.upwardMultiplier.value = upwardMultiplier }, [upwardMultiplier])
  useEffect(() => { uniforms.current.damping.value = damping }, [damping])

  // Build GPU buffers, geometry, material, compute shaders
  const { geometry, material, initCompute, updateCompute } = useMemo(() => {
    const u = uniforms.current
    const noiseTex = createPerlinNoiseTexture()

    // -- GPU storage buffers --
    const posBuf = instancedArray(debrisCount, 'vec3')
    const velBuf = instancedArray(debrisCount, 'vec3')

    // -- Per-instance GPU buffers --
    const scaleBuf = instancedArray(debrisCount, 'float')
    const weightBuf = instancedArray(debrisCount, 'float')
    const baseRotBuf = instancedArray(debrisCount, 'float')
    const typeBuf = instancedArray(debrisCount, 'float') // 0=leaf, 1=crisp, 2=newspaper

    // -- Geometry: subdivided quad for vertex bending --
    const geo = new THREE.PlaneGeometry(1, 1, 2, 2)
    geo.rotateX(-Math.PI * 0.5) // lay flat

    // -- Material --
    const leafColorA = uniform(color(0x95513a)) // brown
    const leafColorB = uniform(color(0xf56a3a)) // orange-red
    const crispColor = uniform(color(0x4488cc)) // placeholder blue-silver
    const newspaperColor = uniform(color(0xe8e0d0)) // placeholder cream

    const mat = new THREE.MeshBasicNodeMaterial({
      side: THREE.DoubleSide,
      // Alpha-tested opaque (not blended): hard cutout, writes depth, no
      // sorted-transparent overdraw. Same fix as the tree leaves.
      transparent: false,
      alphaTest: 0.5,
    })

    // Color node: type-conditional
    mat.colorNode = Fn(() => {
      const debrisType = typeBuf.element(instanceIndex)
      const { isLeaf, isCrisp, isNewspaper } = typeWeights(debrisType)

      // Leaf: procedural brown→orange mix per instance
      const mixStrength = hash(instanceIndex.add(99))
      const leafCol = mix(leafColorA, leafColorB, mixStrength)

      // Crisp: placeholder solid (future: texture(crispTex, uv()))
      const crispCol = vec3(crispColor)

      // Newspaper: placeholder solid (future: texture(newsTex, uv()))
      const newsCol = vec3(newspaperColor)

      return vec3(leafCol).mul(isLeaf)
        .add(crispCol.mul(isCrisp))
        .add(newsCol.mul(isNewspaper))
    })()

    // Opacity node: SDF alpha masking per type
    mat.opacityNode = Fn(() => {
      const debrisType = typeBuf.element(instanceIndex)
      const { isLeaf, isCrisp, isNewspaper } = typeWeights(debrisType)

      const uvCoord = uv()

      // -- Leaf SDF: ellipse that pinches at tips --
      const cx = uvCoord.x.sub(0.5)
      const cy = uvCoord.y.sub(0.5)
      const yFactor = float(1.0).sub(abs(cy).mul(2.0)) // 1 at center, 0 at tips
      const leafWidth = float(0.3).mul(yFactor.mul(yFactor)) // quadratic taper
      const yRadius = float(0.48)
      const ex = cx.div(leafWidth.max(0.001))
      const ey = cy.div(yRadius)
      const ellipseD = ex.mul(ex).add(ey.mul(ey))
      const leafAlpha = smoothstep(float(1.1), float(0.7), ellipseD)

      // -- Crisp SDF: rounded rectangle --
      const centered = abs(uvCoord.sub(vec2(0.5, 0.5)))
      const halfSize = vec2(0.42, 0.35)
      const boxDist = max(centered.x.sub(halfSize.x), centered.y.sub(halfSize.y))
      const crispAlpha = smoothstep(float(0.02), float(-0.02), boxDist)

      // -- Newspaper: full rectangle --
      const newsAlpha = float(1.0)

      return leafAlpha.mul(isLeaf)
        .add(crispAlpha.mul(isCrisp))
        .add(newsAlpha.mul(isNewspaper))
    })()

    // Position node: read from buffer, apply rotation + noise bend
    mat.positionNode = Fn(() => {
      const debrisPosition = posBuf.element(instanceIndex)
      const debrisScale = scaleBuf.element(instanceIndex)
      const baseRot = baseRotBuf.element(instanceIndex)
      const debrisType = typeBuf.element(instanceIndex)

      let newPosition = positionGeometry.mul(debrisScale).mul(u.scale)

      // Elevation-based rotation: higher debris spins more
      const rotMultiplier = max(debrisPosition.y.mul(u.rotationElevationMultiplier), float(0))
      const rotationZ = sin(debrisPosition.x.mul(u.rotationFrequency)).mul(rotMultiplier)
      const rotationX = sin(debrisPosition.z.mul(u.rotationFrequency)).mul(rotMultiplier)

      // Apply 3-axis rotation
      newPosition = vec3(
        rotateUV(newPosition.xy, rotationZ, vec2(0)).x,
        rotateUV(newPosition.xy, rotationZ, vec2(0)).y,
        newPosition.z,
      )
      const afterYZ = rotateUV(vec2(newPosition.y, newPosition.z), rotationX, vec2(0))
      newPosition = vec3(newPosition.x, afterYZ.x, afterYZ.y)
      const afterXZ = rotateUV(vec2(newPosition.x, newPosition.z), baseRot, vec2(0))
      newPosition = vec3(afterXZ.x, newPosition.y, afterXZ.y)

      // -- Noise-based bending (newspaper only, height-gated) --
      const { isNewspaper } = typeWeights(debrisType)
      const bendIntensity = isNewspaper.mul(0.8)

      // Only bend when airborne — remap y: 0 at ground, 1 at y>=3
      const heightFactor = debrisPosition.y.div(3.0).clamp(0, 1)

      // Sample noise at instance world position + animated time
      const noiseUvBend = debrisPosition.xz.mul(0.08).add(vec2(u.bendTime, u.bendTime.mul(0.7)))
      const bendNoise = texture(noiseTex, noiseUvBend).r.sub(0.5).mul(2.0) // -1 to 1

      // Distance from center of plane → edges bend more than middle
      const distFromCenter = abs(positionGeometry.x).add(abs(positionGeometry.z))
      const bendOffset = bendNoise.mul(distFromCenter).mul(bendIntensity).mul(heightFactor).mul(u.scale).mul(debrisScale)
      newPosition.y.addAssign(bendOffset)

      return newPosition.add(debrisPosition)
    })()

    // -- Init compute: scatter on ground, assign types --
    const init = Fn(() => {
      const position = posBuf.element(instanceIndex)
      const velocity = velBuf.element(instanceIndex)
      const scl = scaleBuf.element(instanceIndex)
      const wgt = weightBuf.element(instanceIndex)
      const brt = baseRotBuf.element(instanceIndex)
      const debrisType = typeBuf.element(instanceIndex)

      // Assign type: 70% leaf, 20% crisp, 10% newspaper
      const typeHash = hash(instanceIndex.add(200))
      debrisType.assign(
        step(float(0.7), typeHash)          // 1 if >= 0.7 (crisp or newspaper)
          .add(step(float(0.9), typeHash))  // +1 if >= 0.9 (newspaper)
      )

      // Per-instance base values via hash
      const baseScale = hash(instanceIndex.add(50)).mul(0.5).add(0.5) // 0.5–1.0
      const baseWeight = hash(instanceIndex.add(77)).mul(0.1).add(0.1) // 0.1–0.2
      brt.assign(hash(instanceIndex.add(33)).mul(float(Math.PI * 2))) // 0–2π

      // Type-conditional scale and weight (share one typeWeights call)
      const { isLeaf, isCrisp, isNewspaper } = typeWeights(debrisType)
      // leaf=1x, crisp=1.5x, newspaper=3x
      const scaleMultiplier = isLeaf.mul(1.0).add(isCrisp.mul(1.5)).add(isNewspaper.mul(3.0))
      scl.assign(baseScale.mul(scaleMultiplier))
      // leaf=1x, crisp=1.3x, newspaper=0.7x (lighter, floats more)
      const weightMultiplier = isLeaf.mul(1.0).add(isCrisp.mul(1.3)).add(isNewspaper.mul(0.7))
      wgt.assign(baseWeight.mul(weightMultiplier))

      // Random scatter
      position.assign(vec3(
        hash(instanceIndex).sub(0.5).mul(u.size),
        float(0),
        hash(instanceIndex.add(1)).sub(0.5).mul(u.size),
      ))

      // Add noise offset for organic clustering
      const noiseUv = position.xz.mul(0.02)
      const noise = texture(noiseTex, noiseUv).r
      position.x.addAssign(noise.mul(5))

      // Some debris starts in the air
      const airChance = hash(instanceIndex.add(7))
      position.y.assign(
        step(float(0.7), airChance).mul(hash(instanceIndex.add(13)).mul(6))
      )

      velocity.assign(vec3(0, 0, 0))
    })()

    const initComp = init.compute(debrisCount)

    // -- Update compute: physics per frame --
    // Order matches Bruno Simon exactly:
    //   1. Wind  2. Upward fly  3. Damping  4. Gravity  5. Apply  6. Floor  7. Wrap
    const update = Fn(() => {
      const position = posBuf.element(instanceIndex)
      const velocity = velBuf.element(instanceIndex)
      const weight = weightBuf.element(instanceIndex)
      const debrisType = typeBuf.element(instanceIndex)
      const dt = u.deltaTime

      // Wind surface area multiplier: newspaper catches more wind
      const windAreaMul = selectByType(debrisType, float(1.0), float(1.2), float(2.0))

      // 1. Wind — noise-driven directional push
      const noiseUv = position.xz.mul(u.windFrequency)
        .add(wind.direction.mul(wind.localTime)).xy
      const noise = texture(noiseTex, noiseUv).r
      // Floor at a small positive value (was max(0)) so debris always gets a
      // gentle nudge and never fully stalls into a stagnation line. Tunable.
      const windStrength = wind.strength
        .sub(noise).mul(weight).mul(u.windMultiplier).mul(windAreaMul).max(float(0.012))
      velocity.x.addAssign(wind.direction.x.mul(windStrength))
      velocity.z.addAssign(wind.direction.y.mul(windStrength))

      // 2. Upward fly — horizontal speed ASSIGNS vertical velocity
      const horizSpeed = vec2(velocity.x, velocity.z).length()
      const upwardDim = position.y.remapClamp(0, 6, 1, 0)
      velocity.y.assign(
        min(horizSpeed, float(2)).mul(u.upwardMultiplier).mul(upwardDim)
      )

      // 3. Damping
      const dampFactor = float(1).sub(u.damping.mul(dt))
      velocity.mulAssign(dampFactor)

      // 4. Gravity
      velocity.y.assign(velocity.y.sub(u.gravity.mul(weight)))

      // 5. Apply velocity
      position.addAssign(velocity.mul(dt))

      // 5b. OBB obstacle constraint
      // Transform position into each OBB's local frame via dot products with its basis axes,
      // do an axis-aligned check there, then push out along the shallowest local axis
      // and transform the push back to world space.
      Loop(numObstacles, ({ i }) => {
        const centerVec4 = u.obstacleCenters.element(i)
        const ax4 = u.obstacleAxisX.element(i)
        const ay4 = u.obstacleAxisY.element(i)
        const az4 = u.obstacleAxisZ.element(i)
        const ax = ax4.xyz
        const ay = ay4.xyz
        const az = az4.xyz
        const hx = ax4.w
        const hy = ay4.w
        const hz = az4.w

        // Vector from OBB center to debris, then project onto OBB axes
        const delta = position.sub(centerVec4.xyz)
        const lx = dot(delta, ax)
        const ly = dot(delta, ay)
        const lz = dot(delta, az)

        // Inside if |l*| <= h* on all axes
        const inside = step(abs(lx), hx).mul(step(abs(ly), hy)).mul(step(abs(lz), hz))

        // Penetration along each axis: how far from the boundary
        const penX = hx.sub(abs(lx))
        const penY = hy.sub(abs(ly))
        const penZ = hz.sub(abs(lz))

        const xIsMin = step(penX, penY).mul(step(penX, penZ))
        const zIsMin = step(penZ, penY).mul(float(1).sub(xIsMin))
        const yIsMin = float(1).sub(xIsMin).sub(zIsMin)

        // Push along the shallowest axis in world space
        const pushVec = ax.mul(penX.mul(sign(lx)).mul(xIsMin))
          .add(ay.mul(penY.mul(sign(ly)).mul(yIsMin)))
          .add(az.mul(penZ.mul(sign(lz)).mul(zIsMin)))

        position.assign(position.add(pushVec.mul(inside)))

        // Zero velocity component along the pushed axis
        const vAx = dot(velocity, ax)
        const vAy = dot(velocity, ay)
        const vAz = dot(velocity, az)
        const killVec = ax.mul(vAx.mul(xIsMin))
          .add(ay.mul(vAy.mul(yIsMin)))
          .add(az.mul(vAz.mul(zIsMin)))
        velocity.assign(velocity.sub(killVec.mul(inside)))
      })

      // 6. Floor clamp
      position.y.assign(max(position.y, float(0.15)))

      // 7. Toroidal wrap around focus point
      const halfSize = u.size.mul(0.5)
      position.x.assign(
        mod(position.x.add(halfSize).sub(u.focusPoint.x), u.size)
          .sub(halfSize).add(u.focusPoint.x)
      )
      position.z.assign(
        mod(position.z.add(halfSize).sub(u.focusPoint.y), u.size)
          .sub(halfSize).add(u.focusPoint.y)
      )
    })()

    const updateComp = update.compute(debrisCount)

    return {
      geometry: geo,
      material: mat,
      initCompute: initComp,
      updateCompute: updateComp,
    }
  }, [debrisCount, numObstacles])

  // Dispose old GPU resources when count changes or on unmount
  useEffect(() => {
    return () => {
      geometry.dispose()
      material.dispose()
      initDone.current = false
    }
  }, [geometry, material, initCompute, updateCompute])

  // Run init compute once
  useEffect(() => {
    if (!initCompute || initDone.current) return
    try {
      gl.compute(initCompute)
      initDone.current = true
    } catch (e) {
      console.error('[Debris] Init compute error:', e)
    }
  }, [gl, initCompute])

  // Run update compute synchronously each frame
  useFrame((_, delta) => {
    if (!updateCompute || !initDone.current) return
    uniforms.current.deltaTime.value = Math.min(delta, 0.05)
    uniforms.current.bendTime.value += delta * 0.5
    gl.compute(updateCompute)
  })

  // Wait for obstacles to load before rendering — the shader bakes numObstacles
  // at compile time, so we want to compile once with the final obstacle count.
  if (!obstaclesLoaded) return null

  return (
    <instancedMesh args={[geometry, material, debrisCount]} frustumCulled={false} />
  )
}
